/**
 * Configuration Manager
 * Handles runtime configuration discovery from multiple sources:
 * 1. URL parameters/hash
 * 2. window.EDI_CONFIG
 * 3. Component attributes (handled by web component)
 * 4. Environment variables (development fallback)
 */

export interface EDIConfig {
  supabaseUrl: string;
  jwtToken?: string;
  anonKey?: string;
}

/** Claims extracted from the JWT token. */
export interface JWTClaims {
  customer?: string;
  acl?: Array<{ id: number; name: string }>;
  iss?: string;
  iat?: number;
  exp?: number;
}

/**
 * Decode a JWT payload without verification (validation is done server-side by MyRest/Kong).
 * Returns null if the token is malformed.
 */
export function decodeJWTPayload(token: string): JWTClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

let _jwtClaims: JWTClaims | null = null;

/** Get the decoded JWT claims. Available after initializeSupabase(). */
export function getJWTClaims(): JWTClaims | null {
  return _jwtClaims;
}

/** Get the customer ID from the JWT. This is the user_id for documents. */
export function getCustomerId(): string | undefined {
  return _jwtClaims?.customer;
}

/**
 * The composing account (reports account_id) the host passes via the
 * `account-id` attribute. This is the key for customer_defaults (WDEV-142) —
 * the account dimension, NOT the JWT `customer` claim. US Nameplate uses two
 * accounts: 1620 (regular) and 1621 (Direct Ship).
 */
let _accountId: string | undefined;

export function setAccountId(accountId: string | undefined): void {
  _accountId = accountId && accountId.length > 0 ? accountId : undefined;
}

export function getAccountId(): string | undefined {
  return _accountId;
}

export function setJWTClaims(config: EDIConfig): void {
  if (config.jwtToken) {
    _jwtClaims = decodeJWTPayload(config.jwtToken);
    if (_jwtClaims) {
      console.log('[config] JWT claims:', { customer: _jwtClaims.customer, iss: _jwtClaims.iss });
    } else {
      console.error('[config] Failed to decode JWT token');
    }
  }
}

// Global configuration object (can be set by external systems)
declare global {
  interface Window {
    EDI_CONFIG?: EDIConfig;
  }
}

/**
 * Parse configuration from URL parameters or hash
 * Supports both ?jwt=xxx&supabaseUrl=xxx and #jwt=xxx&supabaseUrl=xxx
 */
function parseURLConfig(): EDIConfig | null {
  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  
  // Try search params first, then hash params
  const params = searchParams.toString() ? searchParams : hashParams;
  
  const jwtToken = params.get('jwt') || params.get('jwtToken') || params.get('jwt_token');
  const supabaseUrl = params.get('supabaseUrl') || params.get('supabase_url');
  const anonKey = params.get('anonKey') || params.get('anon_key');
  
  if (supabaseUrl) {
    return {
      supabaseUrl,
      jwtToken: jwtToken || undefined,
      anonKey: anonKey || undefined,
    };
  }
  
  return null;
}

/**
 * Get configuration from window.EDI_CONFIG
 */
function parseGlobalConfig(): EDIConfig | null {
  if (window.EDI_CONFIG) {
    const config = window.EDI_CONFIG;
    if (config.supabaseUrl) {
      return {
        supabaseUrl: config.supabaseUrl,
        jwtToken: config.jwtToken,
        anonKey: config.anonKey,
      };
    }
  }
  return null;
}

/**
 * Get configuration from environment variables (development fallback)
 */
function parseEnvConfig(): EDIConfig | null {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  if (supabaseUrl) {
    return {
      supabaseUrl,
      anonKey: anonKey || undefined,
    };
  }
  
  return null;
}

/**
 * Get configuration from any available source
 * Priority: URL params > window.EDI_CONFIG > Environment variables
 */
export function getConfig(): EDIConfig | null {
  // Try URL parameters first
  const urlConfig = parseURLConfig();
  if (urlConfig) {
    console.log('🔧 Configuration loaded from URL parameters');
    return urlConfig;
  }
  
  // Try global config object
  const globalConfig = parseGlobalConfig();
  if (globalConfig) {
    console.log('🔧 Configuration loaded from window.EDI_CONFIG');
    return globalConfig;
  }
  
  // Fallback to environment variables (development)
  const envConfig = parseEnvConfig();
  if (envConfig) {
    console.log('🔧 Configuration loaded from environment variables');
    return envConfig;
  }
  
  console.error('❌ No configuration found. Please provide Supabase URL and authentication credentials.');
  return null;
}

/**
 * Validate configuration
 */
export function validateConfig(config: EDIConfig): { valid: boolean; error?: string } {
  if (!config.supabaseUrl) {
    return { valid: false, error: 'Supabase URL is required' };
  }
  
  // Must have either JWT token or anon key
  if (!config.jwtToken && !config.anonKey) {
    return { valid: false, error: 'Either JWT token or anonymous key is required' };
  }
  
  // Validate URL format
  try {
    new URL(config.supabaseUrl);
  } catch {
    return { valid: false, error: 'Invalid Supabase URL format' };
  }
  
  return { valid: true };
}

/**
 * Create configuration from component attributes
 * Used by web component
 */
export function createConfigFromAttributes(attributes: {
  supabaseUrl?: string;
  jwtToken?: string;
  anonKey?: string;
}): EDIConfig | null {
  if (attributes.supabaseUrl) {
    return {
      supabaseUrl: attributes.supabaseUrl,
      jwtToken: attributes.jwtToken,
      anonKey: attributes.anonKey,
    };
  }
  return null;
}
